import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Free Lavender gift: one per diffuser, and never forced back on a shopper who removed it.
 *
 * snippets/st-free-gift-sync.liquid renders <st-free-gift> inside the cart drawer footer
 * with the diffuser count and the gift line's quantity/key. The section is morphed in
 * place after every cart change, so the attributes update and #reconcile runs.
 *
 * Rules (owner's call, 14/09/2026 - a shopper who removes the gift does not want it):
 *  - no diffuser left                         -> gift line removed, and the "dismissed" memory
 *                                                is cleared (next diffuser gets the offer again)
 *  - gift quantity above the diffuser count  -> lowered only when a diffuser was just removed;
 *                                                a count the shopper raised on purpose stays
 *                                                (BXGY: one per diffuser free, extras charged)
 *  - diffuser in the cart, no gift line       -> gift added, UNLESS the shopper removed it
 *                                                earlier in this session (seen going 1+ -> 0
 *                                                while the diffuser count did not change)
 *  - gift quantity below the diffuser count   -> raised only when the diffuser count just went
 *                                                up; a manually lowered gift stays as left
 *
 * Safety: a single in-flight mutation at a time; failures cannot loop; and the drawer is
 * re-rendered from this component's response only when no other cart change started in the
 * meantime - a stale render was showing lines the shopper had just removed.
 *
 * @extends {Component}
 */
class FreeGiftSync extends Component {
  static observedAttributes = ['data-diffusers', 'data-gift-qty', 'data-gift-key', 'data-extra-keys', 'data-gift-lines', 'data-dismissed'];
  /** Cart attribute that remembers a removed gift on the server (underscore = hidden at checkout). */
  // 02/10/2026: two gift elements (Lavender, Peppermint) — each keeps its own memory; one request at a time
  static #globalBusy = false;
  get #DISMISSED_ATTR() { return this.dataset.dismissedAttr || '_st_gift_dismissed'; }
  get #SEEN_KEY() { return 'st-free-gift:diffusers' + (this.dataset.storeSuffix || ''); }
  get #DISMISSED_KEY() { return 'st-free-gift:dismissed' + (this.dataset.storeSuffix || ''); }

  /** @type {AbortController | null} */
  #activeFetch = null;
  #busy = false;
  #queued = false;
  #failures = 0;
  /** @type {number | null} counts from the previous reconcile in this page */
  #lastDiffusers = null;
  #lastGiftQty = null;
  /** Bumped on every cart:lines-update seen on the page, ours included. */
  #cartSeq = 0;
  #onCartLinesUpdate = () => {
    this.#cartSeq++;
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#schedule();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#activeFetch?.abort();
  }

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    const run = () => {
      if (!this.#queued) return;
      this.#queued = false;
      if (this.isConnected) this.#reconcile();
    };
    requestAnimationFrame(run);
    setTimeout(run, 150);
  }

  /** @param {string} key @returns {string | null} */
  #read(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /** @param {string} key @param {string | null} value */
  #write(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch {
      /* private mode etc. - then the memory only lasts for this page load */
    }
  }

  #reconcile() {
    if (this.#busy || this.#failures >= 3) return;
    if (FreeGiftSync.#globalBusy) { setTimeout(() => this.#schedule(), 250); return; }

    const diffusers = Number(this.dataset.diffusers) || 0;
    const giftQty = Number(this.dataset.giftQty) || 0;
    const key = this.dataset.giftKey || '';
    const variant = this.dataset.variant || '';
    // Every gift line as {key, qty, price}. Two lines for one variant is normal here: when
    // the shopper raises the quantity past the free count, Shopify keeps the free units on
    // one line (price 0) and the charged units on another.
    const lines = (this.dataset.giftLines || '')
      .split(',')
      .filter(Boolean)
      .map((s) => {
        const [k, q, p] = s.split('|');
        return { key: k, qty: Number(q) || 0, price: Number(p) || 0 };
      });

    // Previous counts: from this instance, else from the tab (the element may be re-created
    // by a section morph or a navigation, and the memory has to survive that).
    if (this.#lastDiffusers === null) {
      const [d, g] = (this.#read(this.#SEEN_KEY) || '').split(':');
      if (d !== undefined && d !== '') {
        this.#lastDiffusers = Number(d) || 0;
        this.#lastGiftQty = Number(g) || 0;
      }
    }
    const diffusersWentUp = this.#lastDiffusers !== null && diffusers > this.#lastDiffusers;
    const diffusersWentDown = this.#lastDiffusers !== null && diffusers < this.#lastDiffusers;
    // Remembered on the cart itself (any tab, any reload) and, as a fallback, in this tab.
    let dismissed = this.dataset.dismissed === '1' || this.#read(this.#DISMISSED_KEY) === '1';

    // The shopper removed the gift line while keeping the diffuser(s): remember it.
    // (This component only ever sets the gift to 0 when no diffuser is left.)
    if (
      diffusers > 0 &&
      giftQty === 0 &&
      this.#lastGiftQty !== null &&
      this.#lastGiftQty > 0 &&
      this.#lastDiffusers === diffusers
    ) {
      dismissed = true;
      this.#write(this.#DISMISSED_KEY, '1');
      if (this.dataset.dismissed !== '1') {
        // Persist on the cart so a new tab or a reload does not offer the gift again.
        this.#update({ attributes: { [this.#DISMISSED_ATTR]: '1' } }, 0);
        return;
      }
    }

    this.#lastDiffusers = diffusers;
    this.#lastGiftQty = giftQty;
    this.#write(this.#SEEN_KEY, diffusers + ':' + giftQty);

    if (diffusers === 0) {
      this.#write(this.#DISMISSED_KEY, null);
      // One line per pass; the morph after each change re-runs this until none is left. The
      // server-side memory is cleared in the same request (next diffuser gets the offer again).
      const first = lines[0] || (key ? { key } : null);
      const attributes = this.dataset.dismissed === '1' ? { [this.#DISMISSED_ATTR]: '' } : null;
      if (first && giftQty > 0) {
        this.#update({ updates: { [first.key]: 0 }, ...(attributes ? { attributes } : {}) }, 0);
      } else if (attributes) {
        this.#update({ attributes }, 0);
      }
      return;
    }

    // A diffuser was just removed: the bottle that came with it is now a charged line (BXGY
    // only covers one per diffuser), so drop the charged line(s) first, then lower the free
    // line if the total is still above the diffuser count. A count the shopper raised on
    // purpose while the diffusers stayed put is left alone.
    if (giftQty > diffusers && diffusersWentDown) {
      const charged = lines.find((l) => l.price > 0);
      if (charged) {
        this.#change({ id: charged.key, quantity: 0 });
      } else if (key) {
        this.#change({ id: key, quantity: diffusers });
      }
      return;
    }

    if (giftQty === 0) {
      // 29/09/2026: hang it on a diffuser line (nested cart line) - removing that diffuser takes it along
      if (!dismissed && variant) this.#add({ id: Number(variant), quantity: diffusers, ...(this.dataset.parentKey ? { parent_line_key: this.dataset.parentKey } : {}) });
      return;
    }

    if (giftQty < diffusers && key && diffusersWentUp) {
      // Raise the first line by the missing amount (a single line in practice).
      const first = lines.find((l) => l.key === key) || { qty: giftQty };
      this.#change({ id: key, quantity: first.qty + (diffusers - giftQty) });
    }
  }

  /** @param {{ id: string, quantity: number }} line */
  #change(line) {
    return this.#post(Theme.routes.cart_change_url, line, line.quantity);
  }

  /** @param {{ id: number, quantity: number }} line */
  #add(line) {
    return this.#post(Theme.routes.cart_add_url, { items: [line] }, line.quantity);
  }

  /**
   * /cart/update.js: line quantities keyed by line key and/or cart attributes in one request.
   * @param {Record<string, unknown>} payload
   * @param {number} quantity
   */
  #update(payload, quantity) {
    return this.#post(Theme.routes.cart_update_url, payload, quantity);
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} payload
   * @param {number} quantity
   */
  async #post(url, payload, quantity) {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    this.#busy = true;
    FreeGiftSync.#globalBusy = true;

    const sectionId = this.dataset.sectionId || '';
    const deferred = CartLinesUpdateEvent.createPromise();

    // 'update' on purpose: 'add' would pop the drawer open on the cart page.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ merchandiseId: this.dataset.variant || '', quantity }],
        promise: deferred.promise,
      })
    );
    // Our own event has just bumped the counter; remember where we are.
    const seqAtStart = this.#cartSeq;

    try {
      const body = JSON.stringify({
        ...payload,
        ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {}),
      });
      const response = await fetch(url, { ...fetchConfig('json', { body }), signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Free gift sync failed');

      // add.js answers with the added line only; the section HTML is what matters here.
      const cart = url === Theme.routes.cart_add_url ? await (await fetch(Theme.routes.cart_url + '.js')).json() : data;
      // Another cart change started while this one was in flight: its own response will
      // render the drawer, and ours would put back lines the shopper has since removed.
      const fresh = this.#cartSeq === seqAtStart;
      // Fresh section HTML travels with the event so cart-items-component morphs it directly
      // instead of fetching the section again (saves one round trip per correction).
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        ...(fresh && data.sections ? { detail: { sections: data.sections } } : {}),
      });
      this.#failures = 0;
      if (!fresh) return;

      const html = sectionId ? data.sections?.[sectionId] : null;
      if (html) morphSection(sectionId, html, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferred.reject(error);
      if (!(error instanceof Error && error.name === 'AbortError')) {
        this.#failures++;
        console.warn('[st-free-gift]', error);
      }
    } finally {
      this.#busy = false;
      FreeGiftSync.#globalBusy = false;
    }
  }
}

if (!customElements.get('st-free-gift')) {
  customElements.define('st-free-gift', FreeGiftSync);
}
